import * as React from 'react';
import { alpha } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CalculateIcon from '@mui/icons-material/Calculate';
import { BRAND, SHADOW } from '../theme/landingTheme';

// Fixed configs (not random) so the render is stable for prerendering.
// left offset %, size px, duration s, delay s
const PARTICLES = [
  [6, 8, 11, 0], [14, 5, 14, 2.5], [23, 7, 12, 5], [34, 4, 16, 1.2],
  [45, 6, 13, 6.5], [55, 5, 15, 3.8], [64, 8, 11.5, 8], [73, 4, 14.5, 0.8],
  [82, 6, 12.5, 4.4], [91, 5, 15.5, 7.2], [28, 5, 13.5, 9.5], [68, 6, 16.5, 10.4],
];

const scrollToDemo = () => {
  const el = document.getElementById('demo');
  if (el) {
    const top = el.getBoundingClientRect().top + window.pageYOffset - 96;
    window.scrollTo({ top, behavior: 'smooth' });
  }
};

export default function Hero(props) {
  const parallaxRef = React.useRef(null);

  // Subtle parallax: the screenshot drifts up slower than the page scrolls.
  React.useEffect(() => {
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        if (parallaxRef.current) {
          const shift = Math.min(window.scrollY * 0.08, 90);
          parallaxRef.current.style.transform = `translateY(-${shift}px)`;
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Box
      id="hero"
      className="lp-hero-bg"
      sx={{ width: '100%', position: 'relative', overflow: 'hidden' }}
    >
      <div className="lp-blob lp-blob-1" aria-hidden="true" />
      <div className="lp-blob lp-blob-2" aria-hidden="true" />

      {PARTICLES.map(([left, size, duration, delay], i) => (
        <span
          key={i}
          className="lp-particle"
          aria-hidden="true"
          style={{
            left: `${left}%`,
            width: `${size}px`,
            height: `${size}px`,
            animationDuration: `${duration}s`,
            animationDelay: `${delay}s`,
          }}
        />
      ))}

      {/* Price-history line drawing itself across the hero */}
      <svg
        className="lp-chartline"
        viewBox="0 0 1200 300"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M0,240 L90,225 L160,238 L240,190 L330,205 L420,150 L510,170 L600,110 L690,135 L780,90 L880,105 L970,55 L1080,75 L1200,30"
          fill="none"
          stroke={BRAND.yellow}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          className="lp-chartline-slow"
          d="M0,270 L100,262 L200,270 L300,235 L400,248 L500,210 L600,225 L700,185 L800,198 L900,160 L1000,172 L1100,140 L1200,150"
          fill="none"
          stroke={BRAND.yellowDeep}
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>

      {/* Floating product icons at the hero edges */}
      <Box
        className="lp-float-icon"
        aria-hidden="true"
        sx={{
          top: { xs: '16%', md: '24%' },
          left: { xs: '4%', md: '9%' },
          display: { xs: 'none', sm: 'flex' },
        }}
      >
        <TrendingUpIcon fontSize="small" />
      </Box>
      <Box
        className="lp-float-icon lp-float-icon-2"
        aria-hidden="true"
        sx={{
          top: { xs: '12%', md: '20%' },
          right: { xs: '5%', md: '10%' },
          display: { xs: 'none', sm: 'flex' },
        }}
      >
        <NotificationsActiveIcon fontSize="small" />
      </Box>
      <Box
        className="lp-float-icon lp-float-icon-3"
        aria-hidden="true"
        sx={{ top: { xs: '38%', md: '46%' }, right: { xs: '2%', md: '5%' }, display: { xs: 'none', sm: 'flex' } }}
      >
        <CalculateIcon fontSize="small" />
      </Box>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 14, sm: 18 },
          pb: { xs: 8, sm: 12 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Stack spacing={2.5} useFlexGap sx={{ width: { xs: '100%', sm: '80%' }, alignItems: 'center' }}>
          {props.silentJim && (
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                fontSize: 'clamp(1.8rem, 5vw, 2.6rem)',
              }}
            >
              Special discount from{' '}
              <span style={{ color: BRAND.yellowDeep }}>SilentJim</span> —{' '}
              <strong>50% OFF for life!</strong>
              <br />
              <img
                style={{ borderRadius: '200px', width: '96px', marginTop: '8px' }}
                alt="jim"
                src="https://www.nepeto.com/assets/jim.jpg"
              />
            </Typography>
          )}

          <Chip
            icon={<VerifiedIcon sx={{ color: `${BRAND.yellowDeep} !important` }} />}
            label="4,000+ sellers on the Chrome Web Store"
            sx={{
              backgroundColor: BRAND.tint,
              color: BRAND.ink,
              fontWeight: 600,
              px: 1,
            }}
          />

          <Typography
            variant="h1"
            sx={{
              textAlign: 'center',
              fontSize: 'clamp(2.2rem, 8vw, 3.9rem)',
              lineHeight: 1.1,
            }}
          >
            The Product Research Tool Behind{' '}
            <Box
              component="span"
              className="lp-shimmer"
              sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' } }}
            >
              4,000+ Walmart Sellers
            </Box>
          </Typography>

          <Typography
            textAlign="center"
            color="text.secondary"
            sx={{ width: { sm: '100%', md: '76%' }, fontSize: '1.1rem' }}
          >
            Marter is a product research tool for Walmart Marketplace sellers — price
            history, sales estimates, profit calculator and stock alerts in one Chrome
            extension, mobile and desktop app.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignSelf="center"
            spacing={1}
            useFlexGap
            sx={{ pt: 1, width: { xs: '100%', sm: 'auto' } }}
          >
            <TextField
              id="outlined-basic"
              hiddenLabel
              size="small"
              variant="outlined"
              aria-label="Enter your email address"
              placeholder="Your email address"
              name="email"
              sx={{ backgroundColor: '#fff', borderRadius: '10px', minWidth: { sm: '260px' } }}
              inputProps={{
                autoComplete: 'off',
                'aria-label': 'Enter your email address',
              }}
              onChange={(e) => props.setEmail(e.target.value)}
            />
            <Button
              variant="contained"
              className="lp-cta-glow"
              sx={{
                backgroundColor: BRAND.yellow,
                color: BRAND.ink,
                px: 3,
                py: 1,
                width: { xs: '100%', sm: 'auto' },
                boxShadow: SHADOW.cta,
                '&:hover': { backgroundColor: BRAND.yellowDark },
              }}
              onClick={(e) => props.signUp(e)}
            >
              Start Free 7-Day Trial
            </Button>
          </Stack>

          <Typography variant="caption" textAlign="center" sx={{ opacity: 0.8 }}>
            No charge until trial ends&nbsp;·&nbsp;Cancel anytime&nbsp;·&nbsp;By starting you
            agree to our&nbsp;
            <Link href="/terms.pdf" sx={{ color: BRAND.yellowDeep }}>
              Terms &amp; Conditions
            </Link>
            .
          </Typography>

          <Button
            variant="text"
            startIcon={<PlayCircleOutlineIcon />}
            onClick={scrollToDemo}
            sx={{ color: BRAND.ink, fontWeight: 600, opacity: 0.85 }}
          >
            Watch the 2-minute demo
          </Button>
        </Stack>

        <Box
          ref={parallaxRef}
          className="lp-parallax"
          sx={{
            width: { xs: '100%', sm: '85%' },
            mt: { xs: 4, sm: 6 },
            borderRadius: '14px',
            overflow: 'hidden',
            outline: '1px solid',
            outlineColor: alpha('#BFCCD9', 0.5),
            boxShadow: `0 12px 40px ${alpha(BRAND.ink, 0.18)}`,
            backgroundColor: '#fff',
          }}
        >
          <img
            style={{ width: '100%', display: 'block' }}
            src="/marter2.png"
            alt="Marter product research dashboard showing Walmart price history and sales estimates"
          />
        </Box>
      </Container>
    </Box>
  );
}
